 
import * as React from "react"
import { cn } from "@/lib/utils"
import { USER_COLOR_NAMES } from "@/lib/palette"

export const fieldControl =
  "w-full rounded-md border border-input bg-card px-3 text-sm transition-colors placeholder:text-muted-foreground/80 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/15 disabled:cursor-not-allowed disabled:opacity-50"

export function Field({ label, htmlFor, hint, error, optional, children, className }: {
  label: React.ReactNode
  htmlFor: string
  hint?: React.ReactNode
  error?: React.ReactNode
  optional?: boolean
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("space-y-1.5 min-w-0", className)}>
      <label htmlFor={htmlFor} className="block text-[13px] font-medium">
        {label}
        {optional && <span className="font-normal text-muted-foreground"> (opcional)</span>}
      </label>
      {children}
      {hint && !error && <p id={`${htmlFor}-hint`} className="text-[12px] text-muted-foreground">{hint}</p>}
      {error && <p id={`${htmlFor}-error`} role="alert" className="text-[12px] text-negative-700 dark:text-negative-300">{error}</p>}
    </div>
  )
}

export const Select = React.forwardRef<HTMLSelectElement, React.ComponentProps<"select">>(
  ({ className, ...props }, ref) => (
    <select ref={ref} className={cn(fieldControl, "h-10 pr-8", className)} {...props} />
  )
)
Select.displayName = "Select"

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(
  ({ className, ...props }, ref) => (
    <textarea ref={ref} className={cn(fieldControl, "py-2 min-h-[80px] resize-y", className)} {...props} />
  )
)
Textarea.displayName = "Textarea"

/** Campo com prefixo ou sufixo fixo (R$, %). */
export function AffixInput({ prefix, suffix, className, ...props }: React.ComponentProps<"input"> & { prefix?: string; suffix?: string }) {
  return (
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground pointer-events-none">{prefix}</span>}
      <input className={cn(fieldControl, "h-10 num", prefix && "pl-9", suffix && "pr-9", className)} {...props} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground pointer-events-none">{suffix}</span>}
    </div>
  )
}

/** Paleta de cores escolhíveis (radio acessível). */
export function ColorPicker({ colors, value, onChange, label, allowCustom = false }: { colors: readonly string[]; value: string; onChange: (c: string) => void; label: string; allowCustom?: boolean }) {
  const isCustom = !colors.some(c => c.toLowerCase() === value.toLowerCase())
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {colors.map(c => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value.toLowerCase() === c.toLowerCase()}
          aria-label={USER_COLOR_NAMES[c.toUpperCase()] ?? c}
          onClick={() => onChange(c)}
          className={cn(
            "h-7 w-7 rounded-md transition-transform",
            value.toLowerCase() === c.toLowerCase() ? "ring-2 ring-offset-2 ring-offset-card ring-foreground" : "hover:scale-105",
          )}
          style={{ backgroundColor: c }}
        />
      ))}
      {allowCustom && (
        <label
          title="Outra cor"
          className={cn(
            "relative h-7 w-7 rounded-md border border-dashed border-input cursor-pointer flex items-center justify-center text-[13px] text-muted-foreground hover:border-foreground/40",
            isCustom && "border-solid ring-2 ring-offset-2 ring-offset-card ring-foreground",
          )}
          style={isCustom ? { backgroundColor: value } : undefined}
        >
          {!isCustom && "+"}
          <span className="sr-only">Outra cor</span>
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
        </label>
      )}
    </div>
  )
}
